/**
 * Responsibility: point the camera at a barcode and hand back what it says.
 *
 * Deliberately does nothing with the result. One caller puts it in the
 * search box; another puts it in a certificate field. What a code means is
 * the caller's business, and a scanner that decided would be wrong for one
 * of them.
 *
 * The rear camera is asked for, because the one time this runs is standing
 * over a card. Everything is torn down on close: a page that leaves the
 * camera on is a page with a light on the phone and a battery going down.
 *
 * Where the browser cannot decode - Safari on iOS, today - it says so and
 * offers the keyboard instead, rather than showing a dead viewfinder.
 */
import { useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { Barcode } from './barcodes';
import { makeDetector, scanningSupported } from './barcodes';

/** How often to look at the video. Faster buys nothing and costs battery. */
const POLL_MS = 350;

type ScanDialogProps = {
  open: boolean;
  title: string;
  /** What the code will be used for, in one line. */
  hint: string;
  onClose: () => void;
  onScanned: (code: Barcode) => void;
};

export function ScanDialog({ open, title, hint, onClose, onScanned }: ScanDialogProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const supported = scanningSupported();

  useEffect(() => {
    if (!open || !supported) return undefined;

    let stream: MediaStream | null = null;
    let timer: ReturnType<typeof setInterval> | null = null;
    // Guards the async start: the dialog can close before the camera
    // permission is answered, and a stream started after that would never
    // be stopped.
    let cancelled = false;
    const detector = makeDetector();

    const stop = () => {
      if (timer !== null) clearInterval(timer);
      for (const track of stream?.getTracks() ?? []) track.stop();
    };

    const start = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        if (cancelled) {
          stop();
          return;
        }
        const video = videoRef.current;
        if (video === null) return;
        video.srcObject = stream;
        await video.play();

        timer = setInterval(async () => {
          if (detector === null || video.readyState < video.HAVE_ENOUGH_DATA) return;
          try {
            const found = await detector.detect(video);
            if (found.length > 0) {
              // Stopped here rather than in the parent: the parent may
              // keep the dialog open to show what was read.
              stop();
              onScanned(found[0]);
            }
          } catch {
            // A frame that fails to decode is the normal case, not an
            // error worth showing.
          }
        }, POLL_MS);
      } catch (cause) {
        setError(
          cause instanceof Error && cause.name === 'NotAllowedError'
            ? 'Camera permission was refused. Allow it in the browser, or type the number.'
            : 'No camera available. Type the number instead.',
        );
      }
    };

    void start();
    return () => {
      cancelled = true;
      stop();
    };
  }, [open, supported, onScanned]);

  const submitTyped = () => {
    const value = typed.trim();
    if (value.length === 0) return;
    // Reported as an unknown format, because a typed number was not read
    // off anything and nothing should infer a format from it.
    onScanned({ rawValue: value, format: 'typed' });
    setTyped('');
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>{title}</DialogTitle>

      <DialogContent>
        <Stack spacing={1.5}>
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>

          {!supported ? (
            <Alert severity="info">
              This browser cannot read barcodes. Chrome on Android can; Safari cannot yet.
              Type the number instead.
            </Alert>
          ) : null}

          {error ? <Alert severity="warning">{error}</Alert> : null}

          {supported && error === null ? (
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: '4 / 3',
                bgcolor: 'common.black',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <Box
                component="video"
                ref={videoRef}
                muted
                playsInline
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              {/* A window to aim through. Purely a target - the detector
                  reads the whole frame. */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: '28% 12%',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  borderRadius: 1,
                  pointerEvents: 'none',
                }}
              />
            </Box>
          ) : null}

          <TextField
            size="small"
            label="Or type it"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && submitTyped()}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={submitTyped}
          disabled={typed.trim().length === 0}
        >
          Use it
        </Button>
      </DialogActions>
    </Dialog>
  );
}
